import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, useTheme } from "@mui/material";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import ExtensionIcon from "@mui/icons-material/Extension";
import { Trans } from "@lingui/react/macro";
import type { RootState } from "../../store/types.tsx";
import { useFeatureFlag } from "../../hooks/useFeatureFlag.tsx";
import SelectLLMModel from "./SelectLLMModel.tsx";
import MemoryDrawerAction from "./MemoryDrawerAction.tsx";
import NotificationsDrawerAction from "../notifications/NotificationsDrawerAction.tsx";
import {
  addConversation,
  conversationActions,
} from "../../store/conversation.tsx";

interface ConversationDrawerActionsProps {
  closeDrawer: () => void;
  onShowIntegrations?: () => void;
  onConversationSelected?: () => void;
}

const ConversationDrawerActions: React.FC<ConversationDrawerActionsProps> = ({
  closeDrawer,
  onShowIntegrations,
  onConversationSelected,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const availableModels = useSelector(
    (state: RootState) => state.conversationState.availableModels,
  );
  const hasIntegrationsPage = useFeatureFlag("integrations_page");

  const handleIntegrationsClick = () => {
    closeDrawer();
    onShowIntegrations?.();
  };

  const handleAddConversation = () => {
    closeDrawer();
    dispatch(conversationActions.setSelectedView("conversation"));
    dispatch(addConversation() as any);
    onConversationSelected?.();
  };

  return (
    <Box
      marginBottom={2}
      sx={{
        py: "4px",
        backgroundColor: "inherit",
        flexShrink: 0,
      }}
    >
      <Typography
        variant="h4"
        onClick={handleAddConversation}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(1),
          cursor: "pointer",
          whiteSpace: "nowrap",
          fontSize: "1rem !important",
          width: "100%",
          lineHeight: "1",
          margin: "0",
          py: "6px",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
            borderRadius: "25px",
          },
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
          }}
        >
          <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "left",
          }}
        >
          <Trans context="Drawer action label for creating a new conversation">
            New Conversation
          </Trans>
        </Box>
      </Typography>

      <NotificationsDrawerAction
        closeDrawer={closeDrawer}
        onConversationSelected={onConversationSelected}
      />

      <MemoryDrawerAction closeDrawer={closeDrawer} />

      {hasIntegrationsPage && (
        <Typography
          variant="h4"
          onClick={handleIntegrationsClick}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1),
            cursor: "pointer",
            whiteSpace: "nowrap",
            fontSize: "1rem !important",
            width: "100%",
            lineHeight: "1",
            margin: "0",
            py: "6px",
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
              borderRadius: "25px",
            },
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "20px",
              height: "20px",
            }}
          >
            <ExtensionIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "left",
            }}
          >
            <Trans context="Drawer action label for integrations page">
              Integrations
            </Trans>
          </Box>
        </Typography>
      )}

      {availableModels && availableModels.length > 0 && (
        <SelectLLMModel availableModels={availableModels} />
      )}
    </Box>
  );
};

export default ConversationDrawerActions;
